/**
 * Effect id -> the child spans it renders, outermost first.
 *
 * Split out of `bgEffectCss.ts` because that file was holding two different
 * lookups: which CSS an effect needs, and which DOM an effect draws. They grow
 * at the same rate but are read by different callers - `BgEffect.tsx` maps over
 * this one, the static-export pipeline only ever wants the stylesheet - and
 * together they had pushed one file past the size where anyone could find a
 * row in it.
 *
 * Keeping the shape as data is what lets the renderer be one `.map()` instead
 * of one JSX branch per effect. `bgEffectCss.ts` re-exports this map, so every
 * existing import path still works.
 */
import type { BgEffectId } from "../bgEffects.js";

export const BG_EFFECT_PARTS: Record<BgEffectId, string[]> = {
  aurora: ["wa-bge-blob wa-bge-b1", "wa-bge-blob wa-bge-b2", "wa-bge-blob wa-bge-b3"],
  mesh: ["wa-bge-blob wa-bge-b1", "wa-bge-blob wa-bge-b2", "wa-bge-blob wa-bge-b3", "wa-bge-blob wa-bge-b4"],
  conic: ["wa-bge-conic-el"],
  gradient: ["wa-bge-grad-el"],
  spotlight: ["wa-bge-beam wa-bge-beam-1", "wa-bge-beam wa-bge-beam-2", "wa-bge-beam wa-bge-beam-3"],
  stars: [
    "wa-bge-star-l wa-bge-star-1",
    "wa-bge-star-l wa-bge-star-2",
    "wa-bge-star-l wa-bge-star-3",
    "wa-bge-meteor wa-bge-meteor-1",
    "wa-bge-meteor wa-bge-meteor-2",
  ],
  grid: ["wa-bge-grid-glow", "wa-bge-grid-plane"],
  waves: ["wa-bge-wave wa-bge-wave-1", "wa-bge-wave wa-bge-wave-2", "wa-bge-wave wa-bge-wave-3"],
  orbs: [
    "wa-bge-orb wa-bge-orb-1",
    "wa-bge-orb wa-bge-orb-2",
    "wa-bge-orb wa-bge-orb-3",
    "wa-bge-orb wa-bge-orb-4",
    "wa-bge-orb wa-bge-orb-5",
    "wa-bge-orb wa-bge-orb-6",
  ],
  sheen: ["wa-bge-sheen-tint", "wa-bge-sheen-el"],
  pulse: ["wa-bge-halo wa-bge-halo-1", "wa-bge-halo wa-bge-halo-2", "wa-bge-halo wa-bge-halo-3"],
  nebula: ["wa-bge-neb wa-bge-neb-1", "wa-bge-neb wa-bge-neb-2"],
  curtain: [
    "wa-bge-veil wa-bge-veil-1",
    "wa-bge-veil wa-bge-veil-2",
    "wa-bge-veil wa-bge-veil-3",
    "wa-bge-veil wa-bge-veil-4",
  ],
  bokeh: [
    "wa-bge-bok wa-bge-bok-1",
    "wa-bge-bok wa-bge-bok-2",
    "wa-bge-bok wa-bge-bok-3",
    "wa-bge-bok wa-bge-bok-4",
    "wa-bge-bok wa-bge-bok-5",
    "wa-bge-bok wa-bge-bok-6",
    "wa-bge-bok wa-bge-bok-7",
    "wa-bge-bok wa-bge-bok-8",
  ],
  dust: ["wa-bge-mote wa-bge-mote-1", "wa-bge-mote wa-bge-mote-2", "wa-bge-mote wa-bge-mote-3"],
  rays: ["wa-bge-rays-glow", "wa-bge-rays-el wa-bge-rays-1", "wa-bge-rays-el wa-bge-rays-2"],
  ripple: [
    "wa-bge-ripple-core",
    "wa-bge-ring wa-bge-ring-1",
    "wa-bge-ring wa-bge-ring-2",
    "wa-bge-ring wa-bge-ring-3",
    "wa-bge-ring wa-bge-ring-4",
  ],
  dots: ["wa-bge-dotl wa-bge-dotl-1", "wa-bge-dotl wa-bge-dotl-2", "wa-bge-dotl-glow"],
  lines: ["wa-bge-linel wa-bge-linel-1", "wa-bge-linel wa-bge-linel-2", "wa-bge-linel-glow"],
  hex: ["wa-bge-comb wa-bge-comb-1", "wa-bge-comb wa-bge-comb-2"],
  topo: ["wa-bge-cont wa-bge-cont-1", "wa-bge-cont wa-bge-cont-2", "wa-bge-cont-glow"],
  noise: ["wa-bge-noise-tint", "wa-bge-noise-el"],
  blueprint: ["wa-bge-bp wa-bge-bp-1", "wa-bge-bp wa-bge-bp-2", "wa-bge-bp-glow"],
  edge: ["wa-bge-edg wa-bge-edg-1", "wa-bge-edg wa-bge-edg-2"],
  fog: ["wa-bge-fogb wa-bge-fogb-1", "wa-bge-fogb wa-bge-fogb-2", "wa-bge-fogb wa-bge-fogb-3"],
  halftone: ["wa-bge-ht wa-bge-ht-1", "wa-bge-ht wa-bge-ht-2", "wa-bge-ht wa-bge-ht-3"],
  arcs: [
    "wa-bge-arc-core",
    "wa-bge-arc wa-bge-arc-1",
    "wa-bge-arc wa-bge-arc-2",
    "wa-bge-arc wa-bge-arc-3",
    "wa-bge-arc wa-bge-arc-4",
  ],
  circuit: ["wa-bge-trace wa-bge-trace-1", "wa-bge-trace wa-bge-trace-2"],
  iso: ["wa-bge-cube wa-bge-cube-1", "wa-bge-cube-glow"],
  rain: ["wa-bge-drop wa-bge-drop-1", "wa-bge-drop wa-bge-drop-2", "wa-bge-drop wa-bge-drop-3"],
  snow: ["wa-bge-flake wa-bge-flake-1", "wa-bge-flake wa-bge-flake-2", "wa-bge-flake wa-bge-flake-3"],
  glints: [
    "wa-bge-glint-glow",
    "wa-bge-glint wa-bge-glint-1",
    "wa-bge-glint wa-bge-glint-2",
    "wa-bge-glint wa-bge-glint-3",
    "wa-bge-glint wa-bge-glint-4",
    "wa-bge-glint wa-bge-glint-5",
    "wa-bge-glint wa-bge-glint-6",
    "wa-bge-glint wa-bge-glint-7",
  ],
  scan: ["wa-bge-scan-glow", "wa-bge-scanl wa-bge-scanl-1", "wa-bge-scanl wa-bge-scanl-2", "wa-bge-scan-band"],
  caustics: ["wa-bge-caus wa-bge-caus-1", "wa-bge-caus wa-bge-caus-2", "wa-bge-caus wa-bge-caus-3"],
  marble: ["wa-bge-vein-stone", "wa-bge-vein wa-bge-vein-1", "wa-bge-vein wa-bge-vein-2"],
  hatch: ["wa-bge-htc wa-bge-htc-1", "wa-bge-htc wa-bge-htc-2", "wa-bge-htc wa-bge-htc-3"],
  sine: ["wa-bge-swv wa-bge-swv-1", "wa-bge-swv wa-bge-swv-2", "wa-bge-swv wa-bge-swv-3"],
  lowpoly: [
    "wa-bge-poly wa-bge-poly-1",
    "wa-bge-poly wa-bge-poly-2",
    "wa-bge-poly wa-bge-poly-3",
    "wa-bge-poly wa-bge-poly-4",
    "wa-bge-poly wa-bge-poly-5",
    "wa-bge-poly wa-bge-poly-6",
    "wa-bge-poly wa-bge-poly-7",
    "wa-bge-poly wa-bge-poly-8",
  ],
  radar: [
    "wa-bge-rdr-glow",
    "wa-bge-rdr wa-bge-rdr-1",
    "wa-bge-rdr wa-bge-rdr-2",
    "wa-bge-rdr wa-bge-rdr-3",
    "wa-bge-rdr-cross",
    "wa-bge-rdr-sweep",
  ],
  smoke: [
    "wa-bge-plume wa-bge-plume-1",
    "wa-bge-plume wa-bge-plume-2",
    "wa-bge-plume wa-bge-plume-3",
    "wa-bge-plume wa-bge-plume-4",
  ],
  glass: [
    "wa-bge-pane wa-bge-pane-1",
    "wa-bge-pane wa-bge-pane-2",
    "wa-bge-pane wa-bge-pane-3",
    "wa-bge-pane wa-bge-pane-4",
    "wa-bge-pane wa-bge-pane-5",
  ],
  terrain: [
    "wa-bge-ridge-sky",
    "wa-bge-ridge wa-bge-ridge-1",
    "wa-bge-ridge wa-bge-ridge-2",
    "wa-bge-ridge wa-bge-ridge-3",
  ],
  prism: ["wa-bge-prsm-tint", "wa-bge-prsm wa-bge-prsm-1", "wa-bge-prsm wa-bge-prsm-2", "wa-bge-prsm wa-bge-prsm-3"],
  vortex: ["wa-bge-swirl wa-bge-swirl-1", "wa-bge-swirl wa-bge-swirl-2", "wa-bge-swirl wa-bge-swirl-3"],
  warp: [
    "wa-bge-streak wa-bge-streak-1",
    "wa-bge-streak wa-bge-streak-2",
    "wa-bge-streak wa-bge-streak-3",
    "wa-bge-streak-core",
  ],
  droplets: [
    "wa-bge-bead-mist",
    "wa-bge-bead wa-bge-bead-1",
    "wa-bge-bead wa-bge-bead-2",
    "wa-bge-bead wa-bge-bead-3",
    "wa-bge-bead wa-bge-bead-4",
    "wa-bge-bead wa-bge-bead-5",
    "wa-bge-bead wa-bge-bead-6",
    "wa-bge-bead wa-bge-bead-7",
    "wa-bge-bead wa-bge-bead-8",
    "wa-bge-bead wa-bge-bead-9",
  ],
  constellation: ["wa-bge-node wa-bge-node-1", "wa-bge-node wa-bge-node-2"],
  sunset: ["wa-bge-sun-glow", "wa-bge-sun wa-bge-sun-disc", "wa-bge-sun-haze"],
  ink: [
    "wa-bge-blot wa-bge-blot-1",
    "wa-bge-blot wa-bge-blot-2",
    "wa-bge-blot wa-bge-blot-3",
    "wa-bge-blot wa-bge-blot-4",
    "wa-bge-blot wa-bge-blot-5",
  ],
  fireflies: [
    "wa-bge-fly-dusk",
    "wa-bge-fly wa-bge-fly-1",
    "wa-bge-fly wa-bge-fly-2",
    "wa-bge-fly wa-bge-fly-3",
    "wa-bge-fly wa-bge-fly-4",
    "wa-bge-fly wa-bge-fly-5",
    "wa-bge-fly wa-bge-fly-6",
    "wa-bge-fly wa-bge-fly-7",
    "wa-bge-fly wa-bge-fly-8",
  ],
  ember: [
    "wa-bge-spark-bed",
    "wa-bge-spark wa-bge-spark-1",
    "wa-bge-spark wa-bge-spark-2",
    "wa-bge-spark wa-bge-spark-3",
    "wa-bge-spark wa-bge-spark-4",
    "wa-bge-spark wa-bge-spark-5",
    "wa-bge-spark wa-bge-spark-6",
    "wa-bge-spark wa-bge-spark-7",
    "wa-bge-spark wa-bge-spark-8",
    "wa-bge-spark wa-bge-spark-9",
  ],
  leaves: [
    "wa-bge-leaf wa-bge-leaf-1",
    "wa-bge-leaf wa-bge-leaf-2",
    "wa-bge-leaf wa-bge-leaf-3",
    "wa-bge-leaf wa-bge-leaf-4",
    "wa-bge-leaf wa-bge-leaf-5",
    "wa-bge-leaf wa-bge-leaf-6",
    "wa-bge-leaf wa-bge-leaf-7",
  ],
  mosaic: [
    "wa-bge-tile wa-bge-tile-1",
    "wa-bge-tile wa-bge-tile-2",
    "wa-bge-tile wa-bge-tile-3",
    "wa-bge-tile wa-bge-tile-4",
  ],
  plus: ["wa-bge-cross wa-bge-cross-1", "wa-bge-cross wa-bge-cross-2"],
  lightning: ["wa-bge-bolt-cloud", "wa-bge-bolt-sky", "wa-bge-bolt wa-bge-bolt-1", "wa-bge-bolt wa-bge-bolt-2"],
  bars: [
    "wa-bge-bar-floor",
    "wa-bge-bar wa-bge-bar-1",
    "wa-bge-bar wa-bge-bar-2",
    "wa-bge-bar wa-bge-bar-3",
    "wa-bge-bar wa-bge-bar-4",
    "wa-bge-bar wa-bge-bar-5",
    "wa-bge-bar wa-bge-bar-6",
    "wa-bge-bar wa-bge-bar-7",
    "wa-bge-bar wa-bge-bar-8",
    "wa-bge-bar wa-bge-bar-9",
    "wa-bge-bar wa-bge-bar-10",
    "wa-bge-bar wa-bge-bar-11",
    "wa-bge-bar wa-bge-bar-12",
    "wa-bge-bar wa-bge-bar-13",
    "wa-bge-bar wa-bge-bar-14",
  ],
  glitch: [
    "wa-bge-slice-base",
    "wa-bge-slice wa-bge-slice-1",
    "wa-bge-slice wa-bge-slice-2",
    "wa-bge-slice wa-bge-slice-3",
    "wa-bge-slice wa-bge-slice-4",
    "wa-bge-slice wa-bge-slice-5",
    "wa-bge-slice wa-bge-slice-6",
  ],
  blinds: ["wa-bge-slat-room", "wa-bge-slat wa-bge-slat-1", "wa-bge-slat wa-bge-slat-2"],
  neon: [
    "wa-bge-tube-haze",
    "wa-bge-tube wa-bge-tube-1",
    "wa-bge-tube wa-bge-tube-2",
    "wa-bge-tube wa-bge-tube-3",
    "wa-bge-tube wa-bge-tube-4",
    "wa-bge-tube wa-bge-tube-5",
  ],
  skyline: [
    "wa-bge-bldg-sky",
    "wa-bge-bldg wa-bge-bldg-1",
    "wa-bge-bldg wa-bge-bldg-2",
    "wa-bge-bldg wa-bge-bldg-lit",
    "wa-bge-bldg wa-bge-bldg-3",
  ],
  strike: [
    "wa-bge-lgt-cloud",
    "wa-bge-lgt-sheet",
    "wa-bge-lgt-sky",
    "wa-bge-lgt wa-bge-lgt-far",
    "wa-bge-lgt wa-bge-lgt-halo",
    "wa-bge-lgt wa-bge-lgt-core",
  ],
  bubbles: [
    "wa-bge-bub-water",
    "wa-bge-bub wa-bge-bub-1",
    "wa-bge-bub wa-bge-bub-2",
    "wa-bge-bub wa-bge-bub-3",
    "wa-bge-bub wa-bge-bub-4",
    "wa-bge-bub wa-bge-bub-5",
    "wa-bge-bub wa-bge-bub-6",
    "wa-bge-bub wa-bge-bub-7",
    "wa-bge-bub wa-bge-bub-8",
    "wa-bge-bub wa-bge-bub-9",
  ],
  kaleido: [
    "wa-bge-kal wa-bge-kal-1",
    "wa-bge-kal wa-bge-kal-2",
    "wa-bge-kal wa-bge-kal-3",
    "wa-bge-kal-core",
  ],
  helix: [
    "wa-bge-helx-core",
    "wa-bge-helx wa-bge-helx-1",
    "wa-bge-helx wa-bge-helx-2",
  ],
  globe: [
    "wa-bge-glb-halo",
    "wa-bge-glb wa-bge-glb-lat",
    "wa-bge-glb wa-bge-glb-mer",
    "wa-bge-glb wa-bge-glb-shade",
    "wa-bge-glb wa-bge-glb-rim",
  ],
  gears: [
    "wa-bge-cog-plate",
    "wa-bge-cog wa-bge-cog-body wa-bge-cog-1",
    "wa-bge-cog wa-bge-cog-teeth wa-bge-cog-1",
    "wa-bge-cog wa-bge-cog-body wa-bge-cog-2",
    "wa-bge-cog wa-bge-cog-teeth wa-bge-cog-2",
    "wa-bge-cog wa-bge-cog-body wa-bge-cog-3",
    "wa-bge-cog wa-bge-cog-teeth wa-bge-cog-3",
  ],
  checkers: [
    "wa-bge-chk-sky",
    "wa-bge-chk wa-bge-chk-roof",
    "wa-bge-chk wa-bge-chk-floor",
    "wa-bge-chk-glow",
  ],
  matrix: [
    "wa-bge-mtx-glow",
    "wa-bge-mtx wa-bge-mtx-1",
    "wa-bge-mtx wa-bge-mtx-2",
    "wa-bge-mtx wa-bge-mtx-3",
    "wa-bge-mtx wa-bge-mtx-4",
    "wa-bge-mtx wa-bge-mtx-5",
    "wa-bge-mtx wa-bge-mtx-6",
  ],
  confetti: [
    "wa-bge-conf wa-bge-conf-1",
    "wa-bge-conf wa-bge-conf-2",
    "wa-bge-conf wa-bge-conf-3",
    "wa-bge-conf wa-bge-conf-4",
    "wa-bge-conf wa-bge-conf-5",
    "wa-bge-conf wa-bge-conf-6",
    "wa-bge-conf wa-bge-conf-7",
    "wa-bge-conf wa-bge-conf-8",
    "wa-bge-conf wa-bge-conf-9",
    "wa-bge-conf wa-bge-conf-10",
    "wa-bge-conf wa-bge-conf-11",
    "wa-bge-conf wa-bge-conf-12",
  ],
  fireworks: [
    "wa-bge-fw-sky",
    "wa-bge-fw wa-bge-fw-1",
    "wa-bge-fw wa-bge-fw-2",
    "wa-bge-fw wa-bge-fw-3",
    "wa-bge-fw wa-bge-fw-4",
    "wa-bge-fw wa-bge-fw-5",
    "wa-bge-fw wa-bge-fw-6",
    "wa-bge-fw wa-bge-fw-7",
    "wa-bge-fw wa-bge-fw-8",
  ],
  plasma: ["wa-bge-plsm-base", "wa-bge-plsm wa-bge-plsm-1", "wa-bge-plsm wa-bge-plsm-2", "wa-bge-plsm wa-bge-plsm-3"],
  lava: [
    "wa-bge-lav-lamp",
    "wa-bge-lav wa-bge-lav-1",
    "wa-bge-lav wa-bge-lav-2",
    "wa-bge-lav wa-bge-lav-3",
    "wa-bge-lav wa-bge-lav-4",
    "wa-bge-lav wa-bge-lav-5",
  ],
  moire: ["wa-bge-mre-field", "wa-bge-mre wa-bge-mre-1", "wa-bge-mre wa-bge-mre-2", "wa-bge-mre wa-bge-mre-3"],
  shatter: [
    "wa-bge-shr-pane",
    "wa-bge-shr wa-bge-shr-glow",
    "wa-bge-shr wa-bge-shr-line",
    "wa-bge-shr-hit",
    "wa-bge-shr-shock",
  ],
  trails: [
    "wa-bge-trl-night",
    "wa-bge-trl wa-bge-trl-1",
    "wa-bge-trl wa-bge-trl-2",
    "wa-bge-trl wa-bge-trl-3",
    "wa-bge-trl wa-bge-trl-4",
    "wa-bge-trl wa-bge-trl-5",
    "wa-bge-trl wa-bge-trl-6",
    "wa-bge-trl wa-bge-trl-7",
    "wa-bge-trl-bloom",
  ],
  hologram: [
    "wa-bge-holo-cone",
    "wa-bge-holo-r",
    "wa-bge-holo-b",
    "wa-bge-holo-scan",
    "wa-bge-holo-flick",
    "wa-bge-holo-band",
  ],
  crt: ["wa-bge-crt-glass", "wa-bge-crt-grille", "wa-bge-crt-scan", "wa-bge-crt-roll", "wa-bge-crt-vig"],
  heartbeat: ["wa-bge-ecg-amb", "wa-bge-ecg-grid", "wa-bge-ecg-line", "wa-bge-ecg-glow"],
  wormhole: [
    "wa-bge-whl-void",
    "wa-bge-whl-spokes",
    "wa-bge-whl wa-bge-whl-1",
    "wa-bge-whl wa-bge-whl-2",
    "wa-bge-whl wa-bge-whl-3",
    "wa-bge-whl wa-bge-whl-4",
    "wa-bge-whl wa-bge-whl-5",
    "wa-bge-whl wa-bge-whl-6",
    "wa-bge-whl-core",
  ],
  galaxy: [
    "wa-bge-gal-space",
    "wa-bge-gal-dust",
    "wa-bge-gal wa-bge-gal-1",
    "wa-bge-gal wa-bge-gal-2",
    "wa-bge-gal wa-bge-gal-3",
    "wa-bge-gal wa-bge-gal-4",
    "wa-bge-gal wa-bge-gal-5",
    "wa-bge-gal wa-bge-gal-6",
    "wa-bge-gal wa-bge-gal-7",
    "wa-bge-gal wa-bge-gal-8",
    "wa-bge-gal wa-bge-gal-9",
    "wa-bge-gal wa-bge-gal-10",
    "wa-bge-gal-core",
  ],
  atom: [
    "wa-bge-atm-field",
    "wa-bge-atm-ring wa-bge-atm-r1",
    "wa-bge-atm-ring wa-bge-atm-r2",
    "wa-bge-atm-ring wa-bge-atm-r3",
    "wa-bge-atm-core",
    "wa-bge-atm-orb wa-bge-atm-e1",
    "wa-bge-atm-orb wa-bge-atm-e2",
    "wa-bge-atm-orb wa-bge-atm-e3",
  ],
  saturn: [
    "wa-bge-sat-space",
    "wa-bge-sat-stars",
    "wa-bge-sat-ring wa-bge-sat-ring-back",
    "wa-bge-sat-orb wa-bge-sat-globe",
    "wa-bge-sat-orb wa-bge-sat-band",
    "wa-bge-sat-orb wa-bge-sat-shade",
    "wa-bge-sat-ring wa-bge-sat-ring-front",
  ],
  hazard: ["wa-bge-hzd-plate", "wa-bge-hzd wa-bge-hzd-1", "wa-bge-hzd wa-bge-hzd-2", "wa-bge-hzd-beam"],
  rebar: ["wa-bge-rbr-plate", "wa-bge-rbr wa-bge-rbr-h", "wa-bge-rbr wa-bge-rbr-v", "wa-bge-rbr wa-bge-rbr-tie", "wa-bge-rbr-lite"],
  scaffold: [
    "wa-bge-scf-sky",
    "wa-bge-scf wa-bge-scf-frame",
    "wa-bge-scf wa-bge-scf-brace",
    "wa-bge-scf wa-bge-scf-deck",
    "wa-bge-scf-lift",
  ],
  truss: ["wa-bge-trs-sky", "wa-bge-trs wa-bge-trs-far", "wa-bge-trs wa-bge-trs-main", "wa-bge-trs-lite"],
  crane: [
    "wa-bge-crn-sky",
    "wa-bge-crn-mast",
    "wa-bge-crn-jib",
    "wa-bge-crn-weight",
    "wa-bge-crn-cab",
    "wa-bge-crn-lamp",
    "wa-bge-crn-trolley",
    "wa-bge-crn-rope",
    "wa-bge-crn-hook",
  ],
  pipes: [
    "wa-bge-ppl-plate",
    "wa-bge-ppl wa-bge-ppl-v",
    "wa-bge-ppl wa-bge-ppl-h",
    "wa-bge-ppl wa-bge-ppl-joint",
    "wa-bge-ppl wa-bge-ppl-flow",
  ],
  weld: [
    "wa-bge-wld-plate",
    "wa-bge-wld-seam",
    "wa-bge-wld-smoke",
    "wa-bge-wld-glow",
    "wa-bge-wld-spark wa-bge-wld-s1",
    "wa-bge-wld-spark wa-bge-wld-s2",
    "wa-bge-wld-spark wa-bge-wld-s3",
    "wa-bge-wld-spark wa-bge-wld-s4",
    "wa-bge-wld-spark wa-bge-wld-s5",
    "wa-bge-wld-spark wa-bge-wld-s6",
    "wa-bge-wld-spark wa-bge-wld-s7",
    "wa-bge-wld-spark wa-bge-wld-s8",
    "wa-bge-wld-core",
  ],
  containers: [
    "wa-bge-ctr-sky",
    "wa-bge-ctr-beam",
    "wa-bge-ctr-troll",
    "wa-bge-ctr wa-bge-ctr-back",
    "wa-bge-ctr wa-bge-ctr-mid",
    "wa-bge-ctr wa-bge-ctr-front",
    "wa-bge-ctr wa-bge-ctr-cap",
  ],
  conveyor: [
    "wa-bge-cnv-plate",
    "wa-bge-cnv-roll",
    "wa-bge-cnv-belt",
    "wa-bge-cnv-box wa-bge-cnv-box-1",
    "wa-bge-cnv-box wa-bge-cnv-box-2",
    "wa-bge-cnv-box wa-bge-cnv-box-3",
  ],
  highway: [
    "wa-bge-hwy-sky",
    "wa-bge-hwy-lamp",
    "wa-bge-hwy-road",
    "wa-bge-hwy-dash",
    "wa-bge-hwy-streak wa-bge-hwy-streak-1",
    "wa-bge-hwy-streak wa-bge-hwy-streak-2",
    "wa-bge-hwy-streak wa-bge-hwy-streak-3",
  ],
  pallets: ["wa-bge-plt-plate", "wa-bge-plt wa-bge-plt-far", "wa-bge-plt wa-bge-plt-rack", "wa-bge-plt wa-bge-plt-load", "wa-bge-plt-scan"],
  chevron: ["wa-bge-chv-plate", "wa-bge-chv wa-bge-chv-2", "wa-bge-chv wa-bge-chv-1", "wa-bge-chv-lite"],
  barcode: ["wa-bge-bcd-plate", "wa-bge-bcd-label", "wa-bge-bcd-bars", "wa-bge-bcd-num", "wa-bge-bcd-beam"],
  parquet: ["wa-bge-prq-base", "wa-bge-prq wa-bge-prq-a", "wa-bge-prq wa-bge-prq-b", "wa-bge-prq wa-bge-prq-seam", "wa-bge-prq-lite"],
  blockwood: ["wa-bge-blk-base", "wa-bge-blk wa-bge-blk-a", "wa-bge-blk wa-bge-blk-b", "wa-bge-blk-grid", "wa-bge-blk-lite"],
  decking: [
    "wa-bge-dck-base",
    "wa-bge-dck wa-bge-dck-board",
    "wa-bge-dck wa-bge-dck-grain",
    "wa-bge-dck wa-bge-dck-screw",
    "wa-bge-dck-lite",
  ],
  terrazzo: ["wa-bge-trz-base", "wa-bge-trz wa-bge-trz-coarse", "wa-bge-trz wa-bge-trz-mid", "wa-bge-trz wa-bge-trz-fine", "wa-bge-trz-lite"],
  flare: [
    "wa-bge-flr-veil",
    "wa-bge-flr-ghosts",
    "wa-bge-flr-iris",
    "wa-bge-flr-star",
    "wa-bge-flr-streak",
    "wa-bge-flr-core",
  ],
  leak: [
    "wa-bge-lek-wash",
    "wa-bge-lek wa-bge-lek-1",
    "wa-bge-lek wa-bge-lek-2",
    "wa-bge-lek wa-bge-lek-3",
    "wa-bge-lek-burn",
    "wa-bge-lek-grain",
  ],
  iridescent: ["wa-bge-irid-base", "wa-bge-irid-swirl", "wa-bge-irid-film", "wa-bge-irid-spec"],
  ribbon: ["wa-bge-rbn-glow", "wa-bge-rbn wa-bge-rbn-far", "wa-bge-rbn wa-bge-rbn-mid", "wa-bge-rbn wa-bge-rbn-near"],
  frame: [
    "wa-bge-brd-wash",
    "wa-bge-brd-rim",
    "wa-bge-brd-tick",
    "wa-bge-brd wa-bge-brd-glow",
    "wa-bge-brd wa-bge-brd-b1",
    "wa-bge-brd wa-bge-brd-b2",
  ],
  dither: [
    "wa-bge-dth-tone",
    "wa-bge-dth wa-bge-dth-1",
    "wa-bge-dth wa-bge-dth-2",
    "wa-bge-dth wa-bge-dth-3",
    "wa-bge-dth wa-bge-dth-4",
  ],
  dotwave: [
    "wa-bge-dwv-glow",
    "wa-bge-dwv-field",
    "wa-bge-dwv wa-bge-dwv-1",
    "wa-bge-dwv wa-bge-dwv-2",
    "wa-bge-dwv wa-bge-dwv-3",
  ],
  gridbeam: [
    "wa-bge-gbm-wash",
    "wa-bge-gbm-grid",
    "wa-bge-gbm-h wa-bge-gbm-h1",
    "wa-bge-gbm-h wa-bge-gbm-h2",
    "wa-bge-gbm-h wa-bge-gbm-h3",
    "wa-bge-gbm-v wa-bge-gbm-v1",
    "wa-bge-gbm-v wa-bge-gbm-v2",
    "wa-bge-gbm-node",
  ],
  cubes: [
    "wa-bge-cub-field",
    "wa-bge-cub wa-bge-cub-1",
    "wa-bge-cub wa-bge-cub-2",
    "wa-bge-cub wa-bge-cub-3",
    "wa-bge-cub wa-bge-cub-4",
    "wa-bge-cub wa-bge-cub-5",
    "wa-bge-cub wa-bge-cub-6",
    "wa-bge-cub wa-bge-cub-7",
  ],
  waveform: [
    "wa-bge-wfm-glow",
    "wa-bge-wfm wa-bge-wfm-1",
    "wa-bge-wfm wa-bge-wfm-2",
    "wa-bge-wfm wa-bge-wfm-3",
    "wa-bge-wfm wa-bge-wfm-4",
    "wa-bge-wfm wa-bge-wfm-5",
    "wa-bge-wfm wa-bge-wfm-6",
    "wa-bge-wfm wa-bge-wfm-7",
    "wa-bge-wfm wa-bge-wfm-8",
  ],
};
